/* eslint-disable no-console */
import { useState } from 'react';
import { HiOutlineCheck, HiOutlineEye, HiOutlineEyeOff, HiOutlineLockClosed } from 'react-icons/hi';

import { changePassword } from '../../../operations/users';
import {
  ErrorText,
  FormGroup,
  FormInput,
  FormLabel,
  PasswordInputWrapper,
  SaveButton,
  SectionDescription,
  SectionTitle,
  SuccessMessage,
  TogglePasswordButton,
} from './styles';

export interface PasswordValues {
  current_password: string;
  password: string;
  password_confirmation: string;
}

type PasswordField = 'current' | 'new' | 'confirm';

export const PasswordChange = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [visibleFields, setVisibleFields] = useState<Record<PasswordField, boolean>>({
    current: false,
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field: PasswordField) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.current = 'Informe a senha atual';
    }

    if (!newPassword) {
      newErrors.new = 'Informe a nova senha';
    } else if (newPassword.length < 6) {
      newErrors.new = 'A senha deve ter no mínimo 6 caracteres';
    }

    if (!confirmPassword) {
      newErrors.confirm = 'Confirme a nova senha';
    } else if (confirmPassword !== newPassword) {
      newErrors.confirm = 'As senhas não coincidem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(false);

    if (!validate()) return;

    setIsLoading(true);
    try {
      await changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      setShowSuccess(true);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SectionTitle>
        <HiOutlineLockClosed style={{ marginRight: 8 }} />
        Alterar senha
      </SectionTitle>
      <SectionDescription>
        Atualize sua senha de acesso ao painel.
      </SectionDescription>

      {showSuccess && (
        <SuccessMessage>
          <HiOutlineCheck />
          Senha alterada com sucesso!
        </SuccessMessage>
      )}

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <FormLabel htmlFor="current_password">Senha atual</FormLabel>
          <PasswordInputWrapper>
            <FormInput
              id="current_password"
              type={visibleFields.current ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (errors.current) setErrors((prev) => ({ ...prev, current: '' }));
              }}
              placeholder="Insira a senha atual"
            />
            <TogglePasswordButton
              type="button"
              onClick={() => toggleVisibility('current')}
              aria-label={visibleFields.current ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {visibleFields.current ? <HiOutlineEyeOff /> : <HiOutlineEye />}
            </TogglePasswordButton>
          </PasswordInputWrapper>
          {errors.current && <ErrorText>{errors.current}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="new_password">Nova senha</FormLabel>
          <PasswordInputWrapper>
            <FormInput
              id="new_password"
              type={visibleFields.new ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.new) setErrors((prev) => ({ ...prev, new: '' }));
              }}
              placeholder="Insira a nova senha"
            />
            <TogglePasswordButton
              type="button"
              onClick={() => toggleVisibility('new')}
              aria-label={visibleFields.new ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {visibleFields.new ? <HiOutlineEyeOff /> : <HiOutlineEye />}
            </TogglePasswordButton>
          </PasswordInputWrapper>
          {errors.new && <ErrorText>{errors.new}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="confirm_password">Confirmar nova senha</FormLabel>
          <PasswordInputWrapper>
            <FormInput
              id="confirm_password"
              type={visibleFields.confirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirm) setErrors((prev) => ({ ...prev, confirm: '' }));
              }}
              placeholder="Confirme a nova senha"
            />
            <TogglePasswordButton
              type="button"
              onClick={() => toggleVisibility('confirm')}
              aria-label={visibleFields.confirm ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {visibleFields.confirm ? <HiOutlineEyeOff /> : <HiOutlineEye />}
            </TogglePasswordButton>
          </PasswordInputWrapper>
          {errors.confirm && <ErrorText>{errors.confirm}</ErrorText>}
        </FormGroup>

        <SaveButton type="submit" disabled={isLoading}>
          {isLoading ? 'Salvando...' : 'Alterar senha'}
        </SaveButton>
      </form>
    </>
  );
};
